// Tooltip — a small card that rises over whatever it explains (the hotbar's actions). Portalled to
// <body> and placed from the anchor's rect, so a clipped strip (`overflow: hidden`) never cuts it;
// it flips below the anchor when there is no room above and stays inside the window.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  /** The element it explains; nothing shows while undefined. */
  anchor?: HTMLElement | null;
  title?: ReactNode;
  children?: ReactNode;
  /** The fine print (cost, cooldown, keys) under a rule. */
  terms?: ReactNode;
  className?: string;
}

const GAP = 8;
const EDGE = 8;

export function Tooltip({ anchor, title, children, terms, className = '' }: TooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number; below: boolean }>();
  useLayoutEffect(() => {
    if (!anchor || !ref.current) { setAt(undefined); return; }
    const a = anchor.getBoundingClientRect(), t = ref.current.getBoundingClientRect();
    const below = a.top - GAP - t.height < EDGE;
    const left = Math.max(EDGE, Math.min(window.innerWidth - t.width - EDGE, a.left + a.width / 2 - t.width / 2));
    setAt({ left, top: below ? a.bottom + GAP : a.top - GAP - t.height, below });
  }, [anchor, title, children, terms]);
  if (!anchor) return null;
  return createPortal(
    <div ref={ref} role="tooltip" className={`fk-tooltip ${at?.below ? 'fk-tooltip--below' : ''} ${className}`}
      style={at ? { left: at.left, top: at.top } : { left: -9999, top: -9999, visibility: 'hidden' }}>
      {title && <div className="fk-tooltip__title">{title}</div>}
      {children && <div className="fk-tooltip__body">{children}</div>}
      {terms && <div className="fk-tooltip__terms">{terms}</div>}
    </div>,
    document.body,
  );
}
